// Calibration campaign helpers for the Experiment tab. Pure - no React, no fetching.
//
// The backend (padihat/experiment) owns every number: presets, chamber range, estimates, the fitted methods and
// their scores. This file only formats them and mirrors ONE thing: how a fitted method corrects a level
// (`correction`, the same formula as backend experiment/methods.py correction()), so the chart can draw the
// corrected line for any picked method without another request.

export const REF_T_C = 30;
export const REF_RH_PCT = 70;

// A campaign's chamber run: calib-<id> since 21 Oct 2026 (one run for the whole campaign), calib-<id>-c<seq>-<try>
// before (one run per condition). conditionSeqFromRunId is null for the single run.
const CAMPAIGN_RUN = /^calib-(\d+)(?:-c(\d+)(?:-|$)|$)/;

export function campaignIdFromRunId(runId) {
  const match = CAMPAIGN_RUN.exec(runId || "");
  return match ? Number(match[1]) : null;
}

export function conditionSeqFromRunId(runId) {
  const match = CAMPAIGN_RUN.exec(runId || "");
  return match && match[2] !== undefined ? Number(match[2]) : null;
}

export function correction(methodId, coefficients, sensor, nodeT, nodeRh, reference = {}) {
  const c = coefficients || {};
  const refT = reference.t_c ?? REF_T_C;
  const refRh = reference.rh_pct ?? REF_RH_PCT;
  if (methodId === "none") return 0;
  if (methodId === "first_point" || methodId === "median_offset") return c.offset;
  if (methodId === "offset_gain") return sensor - (c.intercept + c.gain * sensor);
  const useT = methodId === "offset_temp" || methodId === "offset_temp_rh";
  const useRh = methodId === "offset_rh" || methodId === "offset_temp_rh";
  if ((useT && typeof nodeT !== "number") || (useRh && typeof nodeRh !== "number")) return null;
  let value = c.offset;
  if (useT) value += c.per_c * (nodeT - refT);
  if (useRh) value += c.per_pct * (nodeRh - refRh);
  return value;
}

export function calibrate(methodId, coefficients, sensor, nodeT, nodeRh, reference) {
  if (typeof sensor !== "number") return null;
  const corr = correction(methodId, coefficients, sensor, nodeT, nodeRh, reference);
  return typeof corr === "number" && Number.isFinite(corr) ? Math.round((sensor - corr) * 100) / 100 : null;
}

// Adds `water_level_whatif_cm` to chart rows: the row's level in `field` corrected by the method. The node's own
// temperature/humidity: ext_t/ext_h when the row has them, else t/h.
export function applyMethodToRows(rows, method, field, reference) {
  if (!method || !method.coefficients) return rows || [];
  return (rows || []).map((row) => {
    if (typeof row[field] !== "number") return row;
    const t = typeof row.ext_t === "number" ? row.ext_t : row.t;
    const rh = typeof row.ext_h === "number" ? row.ext_h : row.h;
    const value = calibrate(method.id, method.coefficients, row[field], t, rh, reference);
    return typeof value === "number" ? { ...row, water_level_whatif_cm: value } : row;
  });
}

// How hard a chamber condition is on the sensor, for the condition editor and the preset cards.
export function harshness({ temp_c: t, rh_pct: rh }) {
  if (!Number.isFinite(t) || !Number.isFinite(rh)) return null;
  if (t >= 50 || rh >= 90 || rh <= 35) return "harsh";
  if (t <= 32 && rh >= 40 && rh <= 75) return "mild";
  return "moderate";
}

export const HARSHNESS_STYLE = {
  mild: "bg-emerald-50 text-emerald-700 border-emerald-200",
  moderate: "bg-amber-50 text-amber-700 border-amber-200",
  harsh: "bg-red-50 text-red-700 border-red-200",
};

// "15, 20, 25" -> {levels: [15, 20, 25]} or {error}
export function parseLevels(text, { min = 13.5, max = 40.5, maxCount = 24 } = {}) {
  const parts = String(text || "")
    .split(/[\s,;]+/)
    .filter(Boolean);
  if (parts.length < 2) return { error: "Enter at least two levels, e.g. 15, 25, 15." };
  if (parts.length > maxCount) return { error: `At most ${maxCount} levels.` };
  const levels = parts.map(Number);
  if (levels.some((v) => !Number.isFinite(v))) return { error: "Levels must be numbers (cm on the tank ruler)." };
  if (levels.some((v) => v < min || v > max)) return { error: `Levels must be ${min}-${max} cm on the ruler.` };
  return { levels };
}

export function conditionError(row, chamber) {
  const t = Number(row.temp_c);
  const rh = Number(row.rh_pct);
  if (row.temp_c === "" || !Number.isFinite(t)) return "Enter a temperature.";
  if (row.rh_pct === "" || !Number.isFinite(rh)) return "Enter a humidity.";
  if (chamber && (t < chamber.temp_min_c || t > chamber.temp_max_c)) {
    return `The chamber reaches ${chamber.temp_min_c}-${chamber.temp_max_c} °C.`;
  }
  if (chamber && (rh < chamber.rh_min_pct || rh > chamber.rh_max_pct)) {
    return `The chamber reaches ${chamber.rh_min_pct}-${chamber.rh_max_pct} %RH.`;
  }
  return null;
}

export function normalizeConditions(rows) {
  return rows.map((row) => ({
    ...(row.label ? { label: String(row.label).trim() } : {}),
    temp_c: Number(row.temp_c),
    rh_pct: Number(row.rh_pct),
  }));
}

export function conditionText(c) {
  const level = c.levels_cm && c.levels_cm.length === 1 ? ` · water ${c.levels_cm[0]} cm` : "";
  return `${c.label ? `${c.label} · ` : ""}${c.temp_c} °C / ${c.rh_pct} %RH${level}`;
}

// "reached its limit: 58.7 °C / 44 %RH" when the chamber could not reach a condition's target and was measured where
// it levelled off; null otherwise.
export function limitText(c) {
  const stats = c?.soak_stats || {};
  if (stats.reached !== "limit" || typeof stats.median_temp_c !== "number") return null;
  return `reached its limit: ${stats.median_temp_c} °C / ${stats.median_rh_pct} %RH`;
}

const REASONS = {
  operator_pause: "Paused by an operator after a condition.",
  operator_stop: "Stopped by an operator.",
  time_limit: "Stopped: the campaign reached its 72 h limit.",
  waiting_budget: "Waiting for tomorrow: the pump used its daily budget.",
  manual_pump: "Someone ran the pump by hand: the water level is no longer known.",
  chamber_run_ended: "The chamber run ended (stopped on this page, or its hold step ran out).",
  chamber_unreachable: "The chamber did not take the condition.",
  // Campaigns before 21 Oct 2026 also stopped for these two; photos never stop a campaign now.
  over_ceiling: "A photo shows the water above the safe ceiling.",
  level_unknown: "No confident photo of the tank: label the newest photo, or check the camera.",
  pump_target_out_of_range: "The next pump step would take the water outside 14-41 cm: check the tank.",
  pump_step_failed: "A pump step failed: check the pump and the tank.",
  not_stable: "The chamber never settled at this condition.",
  operator_skip: "Skipped by an operator.",
  campaign_stopped: "The campaign was stopped during this condition.",
};

export function reasonText(reason, detail) {
  if (detail) return detail;
  if (!reason) return "";
  if (reason.startsWith("start_refused")) return "The controller refused the condition.";
  return REASONS[reason] || reason.replace(/_/g, " ");
}

export const CONDITION_STATE_TEXT = {
  pending: "Waiting",
  setting: "Setting the chamber",
  soaking: "Settling",
  staircase: "Water staircase",
  done: "Done",
  skipped: "Skipped",
  failed: "Failed",
};

export const LEVEL_STATE_TEXT = {
  measuring: "Photographing the tank",
  moving: "Moving the water",
  holding: "Holding still",
  closing: "Closing photo",
  done: "Done",
  failed: "Failed",
};

const SOAK_CHECKS = {
  coverage: "Readings arriving",
  temp_band: "Temperature near target",
  rh_band: "Humidity near target",
  temp_max: "No temperature spikes",
  rh_max: "No humidity spikes",
  levelled_off: "Chamber stopped changing (its limit)",
  soak_time: "Settled long enough",
  sensor_settled: "Sensor temperature settled",
};

export function soakChecks(stats) {
  const checks = stats?.checks || {};
  return Object.keys(SOAK_CHECKS)
    .filter((key) => key in checks)
    .map((key) => ({ key, label: SOAK_CHECKS[key], ok: checks[key] }));
}

// One line saying what the campaign is doing now.
export function currentPhase(campaign) {
  if (!campaign) return "";
  if (campaign.state !== "running") return reasonText(campaign.reason, campaign.detail) || campaign.state;
  const condition = (campaign.conditions || []).find((c) => c.seq === campaign.current_seq);
  if (!condition) return "Finishing";
  if (condition.state === "soaking") {
    const soaked = Math.round((condition.soak_stats?.soaked_s || 0) / 60);
    const needed = Math.round((campaign.stability?.min_soak_s || 0) / 60);
    return `Settling: ${soaked} of ${needed} min`;
  }
  if (condition.state === "staircase") {
    const levels = condition.levels || [];
    const open = levels.find((l) => l.state !== "done" && l.state !== "failed");
    const total = (condition.levels_cm && condition.levels_cm.length) || campaign.ladder?.levels_cm?.length || 0;
    if (total === 1) {
      return open ? `Water at ${open.target_cm} cm: ${LEVEL_STATE_TEXT[open.state] || open.state}` : `Water at ${condition.levels_cm[0]} cm`;
    }
    if (open && open.direction === "rest" && (open.state === "holding" || open.state === "closing")) {
      return `Reading the water at ${open.target_cm} cm, where it already is (level ${open.index + 1} of ${total})`;
    }
    if (open) return `Staircase: ${LEVEL_STATE_TEXT[open.state] || open.state} to ${open.target_cm} cm (level ${open.index + 1} of ${total})`;
    return `Staircase: level ${condition.level_index + 1} of ${total}`;
  }
  return CONDITION_STATE_TEXT[condition.state] || condition.state;
}
